import React, { createContext, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { initLoc, calculateVariables, initTier, initCalcs, initListItems, initVatVars } from '../helper';
const roiCtx = createContext();

export const useROI = () => {
  return useContext(roiCtx);
};

export default function ROIProvider(props) {
  const [calcVars, setCalcVars] = useState({
    dtcProjectedMarketShare: _.clone(initLoc),
    transactionPercentageByLocation: _.clone(initLoc),
    networkFeeWarehouseManagement: _.clone(initLoc),
    networkFeeFreightMovement: {
      SA: _.clone(initLoc),
      EU: _.clone(initLoc),
      US: _.clone(initLoc),
      UK: _.clone(initLoc),
      APAC: _.clone(initLoc)
    },

    percentageRatesByTier: {
      Tier_1: _.clone(initLoc),
      Tier_2: _.clone(initLoc),
      Tier_3: _.clone(initLoc),
      Tier_4: _.clone(initLoc),
      Tier_5: _.clone(initLoc)
    },
    tierVar: _.clone(initTier),
    vatVars: _.clone(initVatVars),
    vatOffset: _.clone(initVatVars),

    selectedNetworkFeeFreightMovement: 'SA'
  });

  const [list, setList] = useState([{ ...initListItems }]);
  const [selectedVendor, setSelectedVendor] = useState({ label: '', value: null, roiModelingSource: 'SA' });
  const [isVendorError, setIsVendorError] = useState(false);
  const [showOnVendorProfile, setShowOnVendorProfile] = useState(false);
  const [standaloneCalc, setStandaloneCalc] = useState(false);
  const [initialActivationFee, setInitialActivationFee] = useState(false);
  const [technologyPremium, setTechnologyPremium] = useState(false);
  const [ecoSystemTransactionFees, setEcoSystemTransactionFees] = useState(false);

  /// Calc WineryAvgRevenue

  let wineryAvgRevenue = 0;

  for (let i = 0; i < list?.length; i++) {
    const item = list[i];
    wineryAvgRevenue += item.calcs.traditionalModel.traditionalMethodTotalRevenueToWinery.excluding;
  }

  ///

  useEffect(() => {
    // Re-calculate all the items when the variable changes
    setList((prev) => {
      let next = _.cloneDeep(prev);

      for (let i = 0; i < next.length; i++) {
        next[i]['calcs'] = calculateVariables(next[i], calcVars, wineryAvgRevenue);
      }

      return next;
    });
  }, [calcVars]);

  // Update the selectedNetworkFeeFreightMovement
  useEffect(() => {
    updateNetworkFeeFreightMovement(selectedVendor.roiModelingSource);
  }, [selectedVendor]);

  const updateCalcVars = (gsVars, cVars) => {
    setCalcVars((prev) => {
      const next = _.cloneDeep(prev);

      for (let i = 0; i < gsVars.length; i++) {
        const item = gsVars[i];
        next[item.key] = item.values;
      }

      // Default to SA
      next['selectedNetworkFeeFreightMovement'] = 'SA';

      if (_.isEmpty(cVars)) {
        return next;
      }

      // Overwrite with the calculation calcVars
      for (const cKey in cVars) {
        next[cKey] = cVars[cKey];
      }

      return next;
    });
  };

  const updateSKUList = (items) => {
    if (Array.isArray(items) === false || items.length === 0) {
      return;
    }

    setList(() => {
      const next = _.cloneDeep(items);

      for (let i = 0; i < next.length; i++) {
        next[i]['calcs'] = calculateVariables(next[i], calcVars, wineryAvgRevenue);
      }

      return next;
    });
  };

  const removeListItem = (idx) => {
    setList((prev) => {
      const next = _.cloneDeep(prev);
      next.splice(idx, 1);

      return next;
    });
  };

  const addNewListItem = () => {
    setList((prev) => {
      const next = _.cloneDeep(prev);
      next.unshift({
        ...initListItems,
        id: typeof next[0] === 'undefined' ? 1 : next[0].id + 1
      });

      return next;
    });
  };

  const setListItemField = (idx, key, loc, value) => {
    setList((prev) => {
      const next = _.cloneDeep(prev);

      const val = value;

      if (loc === null) {
        next[idx][key] = val;
      } else {
        next[idx][key][loc] = val;
      }

      next[idx]['calcs'] = calculateVariables(next[idx], calcVars, wineryAvgRevenue);

      return next;
    });
  };

  const onUpdateCalcVars = (key, loc, value) => {
    setCalcVars((prev) => {
      const next = _.cloneDeep(prev);
      next[key][loc] = value;

      return next;
    });
  };

  const updateNetworkFeeFreightMovement = (source) => {
    setCalcVars((prev) => {
      const next = _.cloneDeep(prev);

      const has = _.has(prev, ['networkFeeFreightMovement', source]);
      if (has === false) {
        // Revert to SA by default
        next['selectedNetworkFeeFreightMovement'] = 'SA';
        return next;
      }

      // Select the freight fee based on the source.
      next['selectedNetworkFeeFreightMovement'] = source;

      return next;
    });
  };

  const updateSelectedVendor = (opt) => {
    setSelectedVendor(opt);
  };

  const updateInitialActivationFee = (opt) => {
    setInitialActivationFee(opt);
  };

  const updateTechnologyPremium = (opt) => {
    setTechnologyPremium(opt);
  };

  const updateEcoSystemTransactionFees = (opt) => {
    setEcoSystemTransactionFees(opt);
  };

  const updateStandaloneCalc = (opt) => {
    setStandaloneCalc(opt);

    if (opt === true) {
      setSelectedVendor({ label: '', value: null, roiModelingSource: 'SA' });
    }
  };

  const updateVar = (varKey) => {
    return (loc, value) => {
      setCalcVars((prev) => {
        const next = _.cloneDeep(prev);
        next[varKey][loc] = value;

        return next;
      });
    };
  };

  const updatePercentageRatesByTier = (tier, loc, value) => {
    setCalcVars((prev) => {
      const next = _.cloneDeep(prev);
      next['percentageRatesByTier'][tier][loc] = value;

      return next;
    });
  };

  const values = {
    calcVars: calcVars,
    skuList: list,
    calculateVariables: calculateVariables,
    updateCalcVars: updateCalcVars,
    updateSKUList: updateSKUList,
    removeListItem: removeListItem,
    addNewListItem: addNewListItem,
    setListItemField: setListItemField,
    onUpdateCalcVars: onUpdateCalcVars,
    updateNetworkFeeFreightMovement: updateNetworkFeeFreightMovement,
    selectedVendor: selectedVendor,
    updateSelectedVendor: updateSelectedVendor,
    showOnVendorProfile: showOnVendorProfile,
    setShowOnVendorProfile: setShowOnVendorProfile,
    standaloneCalc: standaloneCalc,
    updateStandaloneCalc: updateStandaloneCalc,
    initialActivationFee: initialActivationFee,
    updateInitialActivationFee: updateInitialActivationFee,
    technologyPremium: technologyPremium,
    updateTechnologyPremium: updateTechnologyPremium,
    ecoSystemTransactionFees: ecoSystemTransactionFees,
    updateEcoSystemTransactionFees: updateEcoSystemTransactionFees,
    updatePercentageRatesByTier: updatePercentageRatesByTier,
    updateVar: updateVar,
    isVendorError: isVendorError,
    setIsVendorError: setIsVendorError
  };

  return <roiCtx.Provider value={values}>{props.children}</roiCtx.Provider>;
}
